// Bank account number validation.
//
// Two profiles, because the app collects account numbers for two different
// audiences:
//
//   domestic — employees (bank_account_number, Team + Onboarding wizards).
//              Digits only, 5-18, matching Indian bank accounts. The IFSC
//              field sits right beside it, so the market is unambiguous.
//   iban     — clients (account_no, Workspace → Clients). Alphanumeric, 5-34,
//              upper-cased, because a client row also carries currency, VAT ID
//              and timezone, so overseas IBANs are in scope.
//
// The value is a STRING everywhere, never a number: "000123456789" is a real
// account number and an integer round-trip would eat the leading zeros.
//
// Keep in step with backend/utils/accountNumber.js.

export const MODES = Object.freeze({
  domestic: Object.freeze({
    min: 5,
    max: 18,
    charset: /^[0-9]+$/,
    charsetLabel: "numbers",
    upper: false,
  }),
  iban: Object.freeze({
    min: 5,
    max: 34,
    charset: /^[A-Z0-9]+$/,
    charsetLabel: "letters and numbers",
    upper: true,
  }),
});

const modeFor = (mode) => MODES[mode] || MODES.domestic;

/**
 * Strip every separator people type or paste, so a pasted "1234 5678-9012"
 * becomes valid. Covers ordinary spaces, tabs, non-breaking spaces (\s matches
 * those), the ASCII hyphen and the unicode dashes that come out of Word and
 * PDFs.
 */
export const cleanAccountNumber = (value, mode) => {
  const cleaned = String(value ?? "").replace(/[\s\-‐-―]/g, "");
  return modeFor(mode).upper ? cleaned.toUpperCase() : cleaned;
};

/**
 * Validate a value. Returns the cleaned string to submit alongside the verdict,
 * so callers never send the raw input.
 *
 * @returns {{ok: boolean, value: string, error: string|null}}
 */
export const validateAccountNumber = (value, opts = {}) => {
  const { mode = "domestic", required = true, label = "Account number" } = opts;
  const profile = modeFor(mode);
  const cleaned = cleanAccountNumber(value, mode);

  if (!cleaned) {
    return required
      ? { ok: false, value: "", error: `${label} is required.` }
      : { ok: true, value: "", error: null };
  }
  // Charset before length: "too short" is misleading feedback for someone who
  // typed a symbol.
  if (!profile.charset.test(cleaned)) {
    return {
      ok: false,
      value: cleaned,
      error: `${label} can only contain ${profile.charsetLabel}.`,
    };
  }
  if (cleaned.length < profile.min) {
    return {
      ok: false,
      value: cleaned,
      error: `${label} must be at least ${profile.min} characters.`,
    };
  }
  if (cleaned.length > profile.max) {
    return {
      ok: false,
      value: cleaned,
      error: `${label} cannot exceed ${profile.max} characters.`,
    };
  }
  return { ok: true, value: cleaned, error: null };
};

/**
 * What to keep in the field while someone is typing or pasting. Drops the
 * separators and anything outside the charset, then caps at the mode's maximum
 * so input stops at the limit instead of being rejected on submit.
 */
export const sanitizeAccountNumberInput = (value, mode) => {
  const profile = modeFor(mode);
  const cleaned = cleanAccountNumber(value, mode).replace(
    profile.upper ? /[^A-Z0-9]/g : /[^0-9]/g,
    "",
  );
  return cleaned.slice(0, profile.max);
};

/** The maximum stored length for a mode — used to set `maxLength` on inputs. */
export const accountNumberMaxLength = (mode) => modeFor(mode).max;
